import PropTypes from "prop-types";
import { useCallback, useState } from "react";
import { useSelector, useDispatch, shallowEqual } from "react-redux";
import { HTTP_STATUS_CODES, PAYMENT_COUPON_TYPE, PROMO_CODE_STATUS, STRATEGY } from "app/constants";
import PromotionCode, { PROMOTION_STATE } from "app/pages/Booking/PromotionCode/PromotionCode";
import {
	clearPromotionValue as clearPromotionValueAction,
	sendPromotionCode as sendPromotionCodeAction,
} from "app/pages/Booking/PromotionCode/promotionCodeActionCreators";
import { logout as logoutAction } from "app/pages/Auth/AuthActions";
import { useNavigate } from "react-router-dom";
import { Formik } from "formik";
import { useIntl } from "react-intl";
import {
	clearStopoverPromotionValue as clearStopoverPromotionValueAction,
	sendStopoverPromotionCode as sendStopoverPromotionCodeAction,
} from "app/pages/Stopover/stopoverActionCreators";
import {
	clearHotelOnlyPromotionValue as clearHotelOnlyPromotionValueAction,
	sendHotelOnlyPromotionCode as sendHotelOnlyPromotionCodeAction,
} from "app/pages/HotelOnly/hotelOnlyActionCreator";
import { useAnalytics } from "app/utils/analytics/useAnalytics";

const PromotionCodeContainer = props => {
	const { onValidPromoCodeApplied, onFocus, isStopover, isHotelOnly } = props;

	const {
		quotationCode,
		strategy,
		status,
		couponType,
		promotionCode,
		promotionValue,
		disabled,
		userBiid,
		bookingFlowId,
	} = useSelector(mapStateToProps, shallowEqual);

	const dispatch = useDispatch();
	const sendPromotionCode = useCallback(
		(...args) => dispatch(sendPromotionCodeAction(...args)),
		[dispatch]
	);
	const sendStopoverPromotionCode = useCallback(
		(...args) => dispatch(sendStopoverPromotionCodeAction(...args)),
		[dispatch]
	);
	const sendHotelOnlyPromotionCode = useCallback(
		(...args) => dispatch(sendHotelOnlyPromotionCodeAction(...args)),
		[dispatch]
	);
	const clearPromotionValue = useCallback(
		(...args) => dispatch(clearPromotionValueAction(...args)),
		[dispatch]
	);
	const clearStopoverPromotionValue = useCallback(
		(...args) => dispatch(clearStopoverPromotionValueAction(...args)),
		[dispatch]
	);
	const clearHotelOnlyPromotionValue = useCallback(
		(...args) => dispatch(clearHotelOnlyPromotionValueAction(...args)),
		[dispatch]
	);
	const logout = useCallback((...args) => dispatch(logoutAction(...args)), [dispatch]);

	const [isSubmitting, setIsSubmitting] = useState(false);
	const navigate = useNavigate();
	const { track } = useAnalytics();

	const intl = useIntl();

	const initialValues = {
		promoCode: promotionCode,
	};

	const isPromoCodeActive = couponType === PAYMENT_COUPON_TYPE.PROMOCODE;

	const isValidPromocodeApplied =
		isPromoCodeActive && (status === PROMO_CODE_STATUS.SUCCESS || promotionValue > 0);

	const isUnvalidPromocodeApplied =
		isPromoCodeActive && status === PROMO_CODE_STATUS.INVALID_PROMOCODE;

	const sendCodePromo = useCallback(
		(values, formikBag) => {
			setIsSubmitting(true);

			const requestValues = { ...values };

			track("add_voucherCode");

			const requestData = isHotelOnly
				? { ...requestValues, code: bookingFlowId, userBiid }
				: { ...requestValues, code: quotationCode, userBiid };

			const sendPromotionCodeFunction = isHotelOnly
				? sendHotelOnlyPromotionCode
				: isStopover
					? sendStopoverPromotionCode
					: sendPromotionCode;

			sendPromotionCodeFunction(requestData).then(res => {
				setIsSubmitting(false);

				track("promotion_code_apply", {
					httpCode: res?.status,
					status: res?.data?.status,
					promoCode: values?.promoCode,
				});

				if (res.status === HTTP_STATUS_CODES.OK) {
					if (
						((res.data.status === PROMO_CODE_STATUS.SUCCESS && values.promoCode) ||
							res.data.status === PROMO_CODE_STATUS.REMOVED_PROMOCODE) &&
						typeof onValidPromoCodeApplied === "function"
					) {
						onValidPromoCodeApplied(values.promoCode);
					} else if (res.data.status === PROMO_CODE_STATUS.INVALID_PROMOCODE) {
						formikBag.setFieldError(
							"promoCode",
							intl.formatMessage({ id: "promotion.code.error.message" })
						);
						// error message is not shown here but is mandatory to trigger the error state
					}
				} else if (res.status === HTTP_STATUS_CODES.FORBIDDEN) {
					logout({ track });
					navigate({ pathname: "/home/login" }, { replace: true });

					if (strategy !== STRATEGY.OAUTH) {
						navigate("/home/login");
					} else {
						navigate("/technical-error/sso");
					}
				}
			});
		},
		[
			status,
			quotationCode,
			sendPromotionCode,
			onValidPromoCodeApplied,
			logout,
			isStopover,
			isHotelOnly,
			bookingFlowId,
			userBiid,
			navigate,
			strategy,
		]
	);

	const handleFormSubmit = useCallback((values, formikBag) => {
		return sendCodePromo(values, formikBag);
	}, []);

	const onClearPromotionInput = useCallback(() => {
		const clearPromotionCodefuntion = isHotelOnly
			? clearHotelOnlyPromotionValue
			: isStopover
				? clearStopoverPromotionValue
				: clearPromotionValue;

		const clearPayload = isHotelOnly
			? { promoCode: "", code: bookingFlowId, userBiid }
			: { promoCode: "", code: quotationCode };

		clearPromotionCodefuntion(clearPayload);
	}, [quotationCode, isStopover, isHotelOnly, bookingFlowId, userBiid]);

	let promocodeState = PROMOTION_STATE.DEFAULT;

	if (isValidPromocodeApplied) {
		promocodeState = PROMOTION_STATE.SUCCESS;
	} else if (isUnvalidPromocodeApplied) {
		promocodeState = PROMOTION_STATE.ERROR;
	}

	return (
		<Formik initialValues={initialValues} onSubmit={handleFormSubmit} enableReinitialize>
			{({ handleSubmit, values }) => {
				const isFieldEmpty = values.promoCode === "";

				return (
					<PromotionCode
						onFocus={onFocus}
						handleSubmit={handleSubmit}
						promocodeState={promocodeState}
						disableButton={Boolean(disabled || isSubmitting || isFieldEmpty)}
						disableField={Boolean(disabled)}
						onClearInput={onClearPromotionInput}
					/>
				);
			}}
		</Formik>
	);
};

PromotionCodeContainer.propTypes = {
	status: PropTypes.string,
	sendPromotionCode: PropTypes.func,
	sendStopoverPromotionCode: PropTypes.func,
	sendHotelOnlyPromotionCode: PropTypes.func,
	logout: PropTypes.func,
	quotationCode: PropTypes.string,
	strategy: PropTypes.string,
	onValidPromoCodeApplied: PropTypes.func,
	onFocus: PropTypes.func,
	clearPromotionValue: PropTypes.func,
	clearStopoverPromotionValue: PropTypes.func,
	clearHotelOnlyPromotionValue: PropTypes.func,
	couponType: PropTypes.string,
	promotionCode: PropTypes.string,
	promotionValue: PropTypes.number,
	isStopover: PropTypes.bool,
	isHotelOnly: PropTypes.bool,
	bookingFlowId: PropTypes.string,
	userBiid: PropTypes.string,
	disabled: PropTypes.bool,
};

const mapStateToProps = state => {
	return {
		quotationCode: state.quotation.code,
		bookingFlowId: state.hotelOnly.bookingFlowId,
		userBiid: state.auth.userBiid,
		disabled: state.booking.credits > 0,
		status: state.promotion.status,
		promotionValue: state.promotion.promotionValue || 0,
		promotionCode: state.promotion.promotionCode || "",
		strategy: state.partner.strategy,
		couponType: state.booking.coupon,
	};
};

export default PromotionCodeContainer;
